/**
 * TOTP verify page — every login after the first.
 *
 * Two paths:
 *   - Authenticator: POST /auth/totp/verify { code }
 *   - Recovery code: POST /auth/recovery/verify { code }
 *
 * On 409 totp_not_enrolled the server is telling us the operator
 * actually needs to enroll first — bounce them to /totp/enroll.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, ShieldCheck } from "lucide-react";
import {
  asApiError,
  postRecoveryVerify,
  postTotpVerify,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";
import { AuthShell } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Mode = "totp" | "recovery";

export function TOTPVerifyPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [mode, setMode] = useState<Mode>("totp");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "totp") {
        await postTotpVerify(code.trim());
      } else {
        await postRecoveryVerify(code.trim());
      }
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      const body = asApiError(err);
      if (body?.error === "totp_not_enrolled") {
        navigate("/totp/enroll", { replace: true });
        return;
      }
      if (body?.error === "account_locked") {
        setError(
          "Too many failed attempts. This account is locked — try again in 15 minutes.",
        );
      } else if (body?.error === "invalid_code") {
        setError("That code didn't match. Try again.");
      } else if (body) {
        setError(`Verification failed (${body.error}).`);
      } else {
        setError("Verification failed. Check your network.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Two-factor verification"
      description={
        mode === "totp"
          ? "Enter the 6-digit code from your authenticator app."
          : "Enter one of your one-time recovery codes."
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div className="space-y-2">
          <Label htmlFor="code">
            {mode === "totp" ? "Authenticator code" : "Recovery code"}
          </Label>
          <Input
            id="code"
            inputMode={mode === "totp" ? "numeric" : "text"}
            autoComplete="one-time-code"
            pattern={mode === "totp" ? "[0-9]*" : undefined}
            maxLength={mode === "totp" ? 6 : 64}
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={submitting}
            placeholder={mode === "totp" ? "123456" : "abcd-1234-…"}
            className={mode === "totp" ? "text-center font-mono text-2xl tracking-[0.5em]" : undefined}
          />
        </div>
        {error ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {error}
          </div>
        ) : null}
        <Button type="submit" className="w-full" disabled={submitting} size="lg">
          {mode === "totp" ? <ShieldCheck /> : <LifeBuoy />}
          {submitting ? "Verifying…" : "Verify"}
        </Button>
        <Button
          type="button"
          variant="link"
          className="w-full"
          onClick={() => {
            setMode((m) => (m === "totp" ? "recovery" : "totp"));
            setCode("");
            setError(null);
          }}
        >
          {mode === "totp"
            ? "Use a recovery code instead"
            : "Back to authenticator code"}
        </Button>
      </form>
    </AuthShell>
  );
}

export default TOTPVerifyPage;
